<template>
  <div
    class="m-details--prematch"
  >
    <TopHeader
      ref="topHeader"
      v-model="matchListVisible"
      :event="event"
      :sport-id="event.sportId"
      :sport="event.sport"
      :change-text="changeText"
    />

    <PrematchSummary class="pre-match" />

    <MatchContainer>
      <div v-if="event.markets">
        <div class="m-detail-tab-navs">
          <div
            :class="['m-nav-item', {
              active: currentNav === 'market'
            }]"
            @click="changeTab('market', 'MatchDetail_market')"
          >
            <span>Markets</span>
          </div>

          <div
            v-if="isSupportWidget"
            :class="['m-nav-item', {
              active: currentNav === 'stats'
            }]"
            @click="changeTab('stats', 'MatchDetail_stats')"
          >
            <span>Stats</span>
          </div>

          <div
            v-if="isSupportCodes"
            :class="['m-nav-item', {
              active: currentNav === 'codes'
            }]"
            @click="changeTab('codes', 'MatchDetail_CodesBtn')"
          >
            <span>Codes</span>
          </div>
        </div>

        <div class="m-tab-container">
          <keep-alive>
            <template v-if="currentNav === 'market'">
              <MarketListContainer />
            </template>
          </keep-alive>

          <!-- stats页需求刷新 -->
          <template v-if="currentNav === 'stats'">
            <StatWidgets
              :event="event"
              :event-id="eventId"
              :sport-id="event.sportId"
              :round-type="event.roundType"
              :start-time="event.startTime"
              tabs-position="top"
              :nav-fixed-offset-top="44"
            />
          </template>

          <!-- codes页需求刷新 -->
          <template v-else-if="currentNav === 'codes'">
            <CodesContainer
              :detail-event-id="eventId"
              v-bind="{ ...codesQuery }"
            />
          </template>
        </div>
      </div>
    </MatchContainer>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { useEventListener } from '@vueuse/core'
import { throttle } from 'lodash-es'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import { checkSupportPrematchWidget } from '@ms/components/widgets/check.js'
import TopHeader from '../components/TopHeader'
import PrematchSummary from './pagelet/PrematchSummary'
import MatchContainer from '../components/MatchContainer'
import MarketListContainer from '../module/MarketListContainer'

const CodesContainer = () => import(/* webpackChunkName: 'codelist' */ 'wap/views/code-talk/routes/Bookingcode')

export default {
  name: 'PrematchDetails',
  components: {
    TopHeader,
    PrematchSummary,
    MatchContainer,
    MarketListContainer,
    StatWidgets: () => import(/* webpackPrefetch: true, webpackChunkName: 'stat-widgets' */ 'wap/components/StatWidgets'),
    CodesContainer
  },
  setup() {
    const matchDetailStore = useMatchDetailStore()
    const { eventId, event } = storeToRefs(matchDetailStore)

    const changeText = ref(false)
    const topHeader = ref(null)

    useEventListener(
      window,
      'scroll',
      throttle(() => {
        const eleTop = topHeader.value.$el
        const eleprematch = document.querySelector('.pre-match')
        if (eleTop && eleprematch) {
          const top = eleprematch.getBoundingClientRect().top
          const height = eleTop.getBoundingClientRect().height
          const heightPre = eleprematch.getBoundingClientRect().height
          changeText.value = top - height <= -heightPre * 0.58
        }
      }, 300)
    )

    return {
      eventId,
      event,
      changeText,
      topHeader
    }
  },
  data() {
    return {
      currentNav: 'market',
      matchListVisible: false,
      codesQuery: {}
    }
  },
  computed: {
    isSupportWidget() {
      return checkSupportPrematchWidget(this.event.sportId)
    },
    isSupportCodes() {
      return [
        'sr:sport:1', // Soccer
        'sr:sport:2', // Basketball
        'sr:sport:5', // Tennis
        'sr:sport:20' // table tennis
      ].includes(this.event.sportId)
    }
  },
  watch: {
    'event.sportId'(newV) {
      if (newV) this.handleCodesQuery()
    }
  },
  created() {
    this.handleCodesQuery()
  },
  methods: {
    handleCodesQuery() {
      if (!this.isSupportCodes) return

      const { oddslot, oddsInput, period, sportId, marketId } = this.$route.query
      this.codesQuery = {
        oddslot: oddslot && decodeURIComponent(oddslot).split(','),
        oddsInput: oddsInput && decodeURIComponent(oddsInput).split(','),
        period: period && decodeURIComponent(period),
        market: sportId && marketId && { sportId: decodeURIComponent(sportId), marketId: decodeURIComponent(marketId) }
      }

      if (oddslot || oddsInput || period || (sportId && marketId)) this.currentNav = 'codes'
    },
    changeTab(nav, dataLabel) {
      if (nav === this.currentNav) return
      this.currentNav = nav
      this.$emit('change-tab', nav)

      const { oddslot, oddsInput, period, sportId, marketId, ...others } = this.$route.query
      if (nav !== 'codes' && (oddslot || oddsInput || period || sportId || marketId)) {
        this.$router.replace({ query: { ...others } })
      }
      window.mstrace(dataLabel)
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");

.m-details {
  .m-detail-tab-navs {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 10/@rem 8/@rem 10/@rem;
    border-radius:20px 20px 0 0;
    background:#222;

    .m-nav-item {
      position: relative;
      color: #d8d8d8;
      font-size: 16/@rem;
      line-height: 20/@rem;

      &.active {
        color: @primary;
        font-weight: 700;
      }

      &.active::after {
        position: absolute;
        right: 0;
        bottom: -6px;
        left: 0;
        width: 40px;
        height: 4px;
        border-radius:2px;
        margin: 0 auto;
        background:linear-gradient(95.92deg,#ffcb00 3.57%,rgba(255, 203, 0, 27%) 99.8%);
        content: "";
      }
    }
  }

  .m-tab-container {
    color: #fff;
  }

  // codes
  .m-codeTalk-filterBoard.onFixedChange {
    top: 44/@rem !important;
  }

  .m-codeTalk-filterBoard {
    border-radius: 0;
    background: #161616;

    .filters-specific {
      height: 44px;
    }

    .filters-wrap {
      padding: 0 20px;
    }

    .m-codeTalk-filterWarp {
      width: calc(109 / 335 * 100%);

      .filter-title {
        justify-content: space-between;
        padding: 0 6px 0 10px;
      }
    }
  }

  .m-bookingCode-share .m-share-info {
    padding-top: 0;
  }

  .m-bookingCode-warp .m-bookingCode-content {
    z-index: 1100;
  }
}

[data-mode="light"] .m-details .filterBoard-wrap .m-codeTalk-filterBoard {
  background: #fff;

   .filters-specific {
    background: #fff;
  }
}
</style>

<style lang="scss" scoped>
[data-mode="light"] .m-detail-tab-navs {
  background:linear-gradient(180deg, #fff 0%,#f8f8f8 100%);

  .m-nav-item {
    color: #333;
  }

  .m-nav-item.active {
    color: #000;
  }
}
</style>

